import { useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import type { ConsoleSessionRow, ScreenActivityRow, SessionDailyStat, SiteActivityRow, TopActiveUserRow } from './types'
import {
  fetchOnlineSessions, fetchSessionActivity, fetchSessionHistory, fetchSessionStats, fetchTopActiveUsers,
} from './useAdminApi'
import { AdminSearch, AdminTable, Banner, FilterMenu, SectionHeader, Td, Th, type FilterGroup } from './ui'
import { pollWhileVisible } from '../lib/pollWhileVisible'

const POLL_MS = 15_000
const HISTORY_PAGE_SIZE = 50

type SessionsTab = 'online' | 'history' | 'usage' | 'top-users' | 'activity'

const CHANNEL_PILL: Record<string, string> = {
  web: 'bg-blue-50 text-blue-700 border-blue-200',
  mobile: 'bg-violet-50 text-violet-700 border-violet-200',
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0) return 'just now'
  const mins = Math.floor(ms / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function SummaryTile({ label, value, tone }: { label: string; value: number; tone: 'blue' | 'violet' | 'slate' }) {
  const toneCls = {
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    violet: 'border-violet-200 bg-violet-50 text-violet-700',
    slate: 'border-slate-200 bg-slate-50 text-slate-600',
  }[tone]
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${toneCls}`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  )
}

function ChannelPill({ channel }: { channel: string }) {
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium ${CHANNEL_PILL[channel] ?? CHANNEL_PILL.web}`}>
      {channel}
    </span>
  )
}

/** city, country, both, or an em dash -- null reads as "unknown", not "no location", same
 *  posture as a null ip_address elsewhere in this table. */
function locationLabel(r: ConsoleSessionRow): string {
  if (r.city && r.country) return `${r.city}, ${r.country}`
  return r.city ?? r.country ?? '—'
}

function SessionRowsTable({ rows, emptyLabel }: { rows: ConsoleSessionRow[]; emptyLabel: string }) {
  if (!rows.length) {
    return <p className="px-1 py-6 text-center text-sm text-slate-400">{emptyLabel}</p>
  }
  return (
    <AdminTable>
      <thead>
        <tr>
          <Th>Subject</Th>
          <Th>Channel</Th>
          <Th>Auth method</Th>
          <Th>IP address</Th>
          <Th>Location</Th>
          <Th>Signed in</Th>
          <Th>Last seen</Th>
          <Th>Ended</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-slate-50/80">
            <Td mono>{r.subject}</Td>
            <Td><ChannelPill channel={r.channel} /></Td>
            <Td>{r.auth_method}</Td>
            <Td mono>{r.ip_address ?? '—'}</Td>
            <Td>{locationLabel(r)}</Td>
            <Td>{timeAgo(r.created_at)}</Td>
            <Td>{timeAgo(r.last_seen_at)}</Td>
            <Td>{r.ended_at ? timeAgo(r.ended_at) : '—'}</Td>
          </tr>
        ))}
      </tbody>
    </AdminTable>
  )
}

/** Mirrors HealthSection.tsx's pattern exactly: pollWhileVisible's own immediate first
 *  tick covers the mount-time load, so there is one effect, not two. "Online" has to
 *  feel live, same reasoning HealthSection's own 15s cadence does. */
function OnlineSessionsView() {
  const [rows, setRows] = useState<ConsoleSessionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const data = await fetchOnlineSessions()
      setRows(data)
      setLastRefreshed(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load online sessions')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    setLoading(true)
    return pollWhileVisible(() => void load(), POLL_MS)
  }, [load])

  const webCount = rows.filter((r) => r.channel === 'web').length
  const mobileCount = rows.filter((r) => r.channel === 'mobile').length

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => void load()}
          className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          <RefreshCw size={13} />
          Refresh
        </button>
        {lastRefreshed ? (
          <span className="text-xs text-slate-400">Updated {timeAgo(lastRefreshed.toISOString())}</span>
        ) : null}
      </div>

      {error ? <Banner tone="error">{error}</Banner> : null}

      <div className="grid grid-cols-3 gap-3">
        <SummaryTile label="Online now" value={rows.length} tone="slate" />
        <SummaryTile label="Web" value={webCount} tone="blue" />
        <SummaryTile label="Mobile" value={mobileCount} tone="violet" />
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <SessionRowsTable rows={rows} emptyLabel="Nobody is currently online." />
      )}
    </div>
  )
}

/** Mirrors UsersSection.tsx/AccessSection.tsx's fetch-once-on-filter-change pattern --
 *  this is historical audit data, not a live dashboard, so it has no reason to poll. */
function SessionHistoryView() {
  const [rows, setRows] = useState<ConsoleSessionRow[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [subject, setSubject] = useState('')
  const [channel, setChannel] = useState<'all' | 'web' | 'mobile'>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { rows: r, total: t } = await fetchSessionHistory({
        subject: subject.trim() || undefined,
        channel: channel === 'all' ? undefined : channel,
        limit: HISTORY_PAGE_SIZE,
        offset,
      })
      setRows(r)
      setTotal(t)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load session history')
    } finally {
      setLoading(false)
    }
  }, [subject, channel, offset])

  useEffect(() => { void load() }, [load])
  // Any filter change restarts from the first page -- a stale offset past the new
  // filtered total would otherwise just read as "no results".
  useEffect(() => { setOffset(0) }, [subject, channel])

  const channelGroup: FilterGroup = {
    id: 'channel', label: 'Channel', value: channel,
    options: [{ id: 'all', label: 'All' }, { id: 'web', label: 'Web' }, { id: 'mobile', label: 'Mobile' }],
    onChange: (id) => setChannel(id as 'all' | 'web' | 'mobile'),
  }

  const rangeLabel = total === 0 ? '0 of 0' : `${offset + 1}–${Math.min(offset + HISTORY_PAGE_SIZE, total)} of ${total}`

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <AdminSearch value={subject} onChange={setSubject} label="Search by subject" placeholder="Search by subject…" />
        <FilterMenu groups={[channelGroup]} onClear={() => setChannel('all')} />
      </div>

      {error ? <Banner tone="error">{error}</Banner> : null}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <SessionRowsTable rows={rows} emptyLabel="No sessions match this filter." />
      )}

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>Showing {rangeLabel}</span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - HISTORY_PAGE_SIZE))}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={offset + HISTORY_PAGE_SIZE >= total}
            onClick={() => setOffset((o) => o + HISTORY_PAGE_SIZE)}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}

/** One small hand-rolled line+area chart, modeled directly on
 *  console/src/charging/ChargingTimeline.tsx's "planned aggregate charger load" lane --
 *  the only existing chart in this codebase, and the only pattern worth copying rather
 *  than reinventing: viewBox in data-unit coordinates (days, not pixels), a y(value)
 *  scaling closure, a manual SVG path for the line, a gradient-filled area closed to the
 *  baseline, a dashed average-reference line, and div-overlay tooltips (not SVG
 *  <title>, not a charting library -- none is installed, and two series don't justify
 *  adding one). */
function DailySeriesChart({
  label, values, color, fillId,
}: {
  label: string
  values: { day: string; value: number }[]
  color: string
  fillId: string
}) {
  const laneH = 72
  const padTop = 10
  const padBottom = 6
  const max = Math.max(1, ...values.map((v) => v.value))
  const avg = values.length ? values.reduce((s, v) => s + v.value, 0) / values.length : 0
  const y = (v: number): number => laneH - padBottom - (v / (max * 1.08)) * (laneH - padTop - padBottom)
  const n = Math.max(1, values.length - 1)
  const x = (i: number): number => (i / n) * 100

  const linePath = values
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(2)} ${y(v.value).toFixed(2)}`)
    .join(' ')
  const areaPath = values.length
    ? `${linePath} L ${x(values.length - 1).toFixed(2)} ${laneH - padBottom} L ${x(0).toFixed(2)} ${laneH - padBottom} Z`
    : ''

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <span className="font-mono text-sm text-slate-700">max {Math.round(max)}</span>
      </div>
      <div className="relative" style={{ height: laneH }}>
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox={`0 0 100 ${laneH}`}
          preserveAspectRatio="none"
          aria-label={`${label} per day`}
        >
          <defs>
            <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.30} />
              <stop offset="100%" stopColor={color} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          {areaPath ? <path d={areaPath} fill={`url(#${fillId})`} /> : null}
          <path d={linePath} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
          {values.length ? (
            <line
              x1={0} x2={100} y1={y(avg)} y2={y(avg)}
              stroke={color} strokeWidth={1} strokeDasharray="4 4" opacity={0.4}
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
        </svg>
        {values.map((v, i) => (
          <div
            key={v.day}
            className="absolute inset-y-0"
            title={`${v.day} — ${v.value}`}
            style={{ left: `${x(i) - 50 / n}%`, width: `${100 / n}%` }}
          />
        ))}
      </div>
    </div>
  )
}

/** Fetch-once-per-window-change -- the window selector (30/60/90 days) is the only input,
 *  same shape as DarReportSection's own month selector. */
function UsageView() {
  const [days, setDays] = useState(30)
  const [stats, setStats] = useState<SessionDailyStat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    fetchSessionStats(days)
      .then((data) => { if (alive) setStats(data) })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : 'Failed to load usage stats') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [days])

  const sessionSeries = useMemo(() => stats.map((s) => ({ day: s.day, value: s.session_count })), [stats])
  const userSeries = useMemo(() => stats.map((s) => ({ day: s.day, value: s.unique_users })), [stats])

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {[30, 60, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
              days === d ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {error ? <Banner tone="error">{error}</Banner> : null}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : stats.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-slate-400">No sessions recorded in this window.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <DailySeriesChart label="Sessions per day" values={sessionSeries} color="#2563EB" fillId="sessionsFill" />
          <DailySeriesChart label="Unique users per day" values={userSeries} color="#7C3AED" fillId="usersFill" />
        </div>
      )}
    </div>
  )
}

/** "Most active user, top 10" -- login count (web + mobile combined), windowed the same
 *  30/60/90-day way UsageView's own window selector works, since both read from the same
 *  retained history (90 days, tools/purge_old_sessions.py). Fetch-once-per-window-change,
 *  same reasoning as UsageView: this is a ranking over history, not a live dashboard. */
function TopUsersView() {
  const [days, setDays] = useState(90)
  const [rows, setRows] = useState<TopActiveUserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    fetchTopActiveUsers(days, 10)
      .then((data) => { if (alive) setRows(data) })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : 'Failed to load top users') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [days])

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {[30, 60, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
              days === d ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {error ? <Banner tone="error">{error}</Banner> : null}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-slate-400">No sessions recorded in this window.</p>
      ) : (
        <AdminTable>
          <thead>
            <tr>
              <Th>Rank</Th>
              <Th>Subject</Th>
              <Th>Sessions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <tr key={r.subject} className="hover:bg-slate-50/80">
                <Td mono>{i + 1}</Td>
                <Td mono>{r.subject}</Td>
                <Td>{r.session_count}</Td>
              </tr>
            ))}
          </tbody>
        </AdminTable>
      )}
    </div>
  )
}

/** "Which screens/sites are most active" -- top screens (site + tab) and top sites,
 *  side by side, same two-table layout UsageView's own two charts use. Fetch-once-per-
 *  window-change, same reasoning as TopUsersView/UsageView above. */
function ActivityView() {
  const [days, setDays] = useState(90)
  const [topScreens, setTopScreens] = useState<ScreenActivityRow[]>([])
  const [topSites, setTopSites] = useState<SiteActivityRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    fetchSessionActivity(days, 10)
      .then((data) => {
        if (!alive) return
        setTopScreens(data.topScreens)
        setTopSites(data.topSites)
      })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : 'Failed to load activity') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [days])

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {[30, 60, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
              days === d ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {error ? <Banner tone="error">{error}</Banner> : null}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Top screens</h3>
            {topScreens.length === 0 ? (
              <p className="px-1 py-6 text-center text-sm text-slate-400">No activity recorded in this window.</p>
            ) : (
              <AdminTable>
                <thead>
                  <tr>
                    <Th>Site</Th>
                    <Th>Screen</Th>
                    <Th>Views</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {topScreens.map((r) => (
                    <tr key={`${r.site}:${r.screen}`} className="hover:bg-slate-50/80">
                      <Td mono>{r.site}</Td>
                      <Td mono>{r.screen}</Td>
                      <Td>{r.view_count}</Td>
                    </tr>
                  ))}
                </tbody>
              </AdminTable>
            )}
          </div>
          <div>
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Top sites</h3>
            {topSites.length === 0 ? (
              <p className="px-1 py-6 text-center text-sm text-slate-400">No activity recorded in this window.</p>
            ) : (
              <AdminTable>
                <thead>
                  <tr>
                    <Th>Site</Th>
                    <Th>Views</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {topSites.map((r) => (
                    <tr key={r.site} className="hover:bg-slate-50/80">
                      <Td mono>{r.site}</Td>
                      <Td>{r.view_count}</Td>
                    </tr>
                  ))}
                </tbody>
              </AdminTable>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/** Super-admin only -- enforced server-side too (403 on all three /api/auth/sessions/*
 *  routes for anyone else), same gate every other super-admin-only section already
 *  uses. Web sessions (Entra/local/SSO) and the Triage mobile app's bearer sessions,
 *  unified into one view -- see serving/auth.py's "web sessions" section for the
 *  session_id threading and serving/auth.py's "mobile bearer tokens" section for the
 *  pre-existing mobile half this reuses. */
export function SessionsSection({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [tab, setTab] = useState<SessionsTab>('online')

  if (!isSuperAdmin) {
    return (
      <p className="text-sm text-slate-500">
        Session history is limited to the platform's super administrator.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Sessions"
        subtitle="Who is online, from where, and the full sign-in history across web and mobile."
      />

      <div className="flex gap-1 border-b border-slate-200">
        {([
          ['online', 'Online now'],
          ['history', 'History'],
          ['usage', 'Usage'],
          ['top-users', 'Top users'],
          ['activity', 'Activity'],
        ] as [SessionsTab, string][]).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-3 py-2 text-sm font-medium ${
              tab === id ? 'border-b-2 border-blue-500 text-blue-700' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'online' ? <OnlineSessionsView /> : null}
      {tab === 'history' ? <SessionHistoryView /> : null}
      {tab === 'usage' ? <UsageView /> : null}
      {tab === 'top-users' ? <TopUsersView /> : null}
      {tab === 'activity' ? <ActivityView /> : null}
    </div>
  )
}
